/* Canonical, per-route SEO registry.

   This is the SOURCE OF TRUTH for meta tags, canonical URLs, Open Graph,
   Twitter and JSON-LD structured data. It ships with the JS bundle so
   `react-snap` (build-time pre-render) can bake the correct tags into
   the HTML of every route. The runtime backend override (via
   /api/public/seo) is a soft fallback for admin edits — it MUST NOT be
   relied on to establish essential SEO metadata.

   Rule of thumb: whenever a public marketing route is added, an entry
   MUST also be added here. The pytest raw-HTML test suite enforces this.
*/

export const CANONICAL_ORIGIN = "https://fastprototype.ai";
export const BRAND_NAME = "FastPrototype.ai";
export const SOCIAL_IMAGE = `${CANONICAL_ORIGIN}/brand/fastprototype-social-1200x630.png`;
export const HERO_IMAGE_ABS = `${CANONICAL_ORIGIN}/brand/hero.png`;

// The date corresponds to when the corrected SEO overhaul was authored
// (used as `datePublished` fallback for guide pages that lack an admin-set
// custom timestamp). Update when the guide content is substantively rewritten.
export const CONTENT_LAST_UPDATED_ISO = "2026-07-31";

function abs(path) {
  return `${CANONICAL_ORIGIN}${path}`;
}

function orgJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: BRAND_NAME,
    url: CANONICAL_ORIGIN,
    logo: `${CANONICAL_ORIGIN}/brand/fastprototype-logo.png`,
    description: "European MVP studio combining AI code generation with senior-developer review to ship investor-ready prototypes in 1–4 weeks.",
    sameAs: [],
  };
}

function websiteJsonLd() {
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: BRAND_NAME,
    url: CANONICAL_ORIGIN,
    potentialAction: {
      "@type": "SearchAction",
      target: `${CANONICAL_ORIGIN}/build?idea={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  };
}

function serviceJsonLd({ name, description, url }) {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    name,
    description,
    url,
    provider: { "@type": "Organization", name: BRAND_NAME, url: CANONICAL_ORIGIN },
    areaServed: "Worldwide",
    serviceType: "MVP software development",
  };
}

function breadcrumbList(items) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: abs(item.path),
    })),
  };
}

function articleJsonLd({ headline, description, url, image, datePublished, dateModified }) {
  return {
    "@context": "https://schema.org",
    "@type": "Article",
    headline,
    description,
    mainEntityOfPage: url,
    url,
    image,
    author: { "@type": "Organization", name: BRAND_NAME, url: CANONICAL_ORIGIN },
    publisher: {
      "@type": "Organization",
      name: BRAND_NAME,
      logo: { "@type": "ImageObject", url: `${CANONICAL_ORIGIN}/brand/fastprototype-logo.png` },
    },
    datePublished,
    dateModified,
  };
}

// -----------------------------------------------------------------------------
// Route registry
// -----------------------------------------------------------------------------
export const SEO_REGISTRY = {
  "/": {
    title: "AI MVP Development Services in 1–4 Weeks | FastPrototype",
    description: "Launch a senior-reviewed MVP in 1–4 weeks. Fixed scope, fixed price, full code ownership, documentation and EU-grade privacy.",
    keywords: "AI MVP development, MVP services, rapid prototyping, fixed-price MVP, startup MVP, senior developer review",
    ogType: "website",
    ogTitle: "AI MVP Development Services in 1–4 Weeks | FastPrototype",
    ogDescription: "Launch a senior-reviewed MVP in 1–4 weeks. Fixed scope, fixed price, full code ownership.",
    jsonLd: [orgJsonLd(), websiteJsonLd()],
  },
  "/about": {
    title: "About FastPrototype | Senior-Reviewed AI MVP Development",
    description: "Meet the team and delivery approach behind FastPrototype's rapid, senior-reviewed MVP development service for startup founders.",
    keywords: "FastPrototype team, European MVP studio, senior developers, GDPR development",
    ogType: "website",
    ogTitle: "About FastPrototype.ai",
    ogDescription: "European MVP studio combining AI codegen with senior-dev oversight.",
    jsonLd: [orgJsonLd()],
  },
  "/build": {
    title: "Build Your MVP | Scope, Timeline and Fixed-Price Quote",
    description: "Describe your product idea and receive a practical MVP scope, recommended features, delivery package and indicative quote.",
    keywords: "build MVP, MVP scope, MVP quote, project builder, fixed-price proposal",
    ogType: "website",
    ogTitle: "Build Your MVP — FastPrototype",
    ogDescription: "From idea to fixed-price MVP scope in minutes.",
    jsonLd: [orgJsonLd()],
  },
  "/legal": {
    title: "Terms and Delivery Boundaries | FastPrototype",
    description: "Review the terms, ownership provisions, delivery boundaries and responsibilities that apply to FastPrototype MVP projects.",
    keywords: "NDA, IP ownership, MVP terms, delivery boundaries, privacy policy",
    ogType: "website",
    ogTitle: "Terms and Delivery Boundaries",
    ogDescription: "NDA, IP ownership, delivery boundaries and GDPR-compliant privacy.",
    jsonLd: [orgJsonLd()],
  },
  "/promoter": {
    title: "Promoter Login — FastPrototype Partner Program",
    description: "Sign in to your FastPrototype promoter dashboard to track referrals, commissions and payouts.",
    keywords: "promoter login, affiliate dashboard, referral tracking",
    ogType: "website",
    ogTitle: "Promoter Login — FastPrototype",
    ogDescription: "Track your referrals, commissions and payouts.",
    jsonLd: [orgJsonLd()],
  },
  "/promoters": {
    title: "FastPrototype Partner Program | Refer Startup Founders",
    description: "Refer founders who need rapid MVP development through the FastPrototype partner and promoter program. Earn 15% on every paid invoice.",
    keywords: "affiliate program, referral, MVP promoter, 15% commission, partner program, startup referral",
    ogType: "website",
    ogTitle: "Earn 15% referring founders — FastPrototype Partner Program",
    ogDescription: "Refer founders who need rapid MVP development. Earn 15% on every paid invoice.",
    jsonLd: [orgJsonLd()],
  },
  "/pricing": {
    title: "MVP Pricing | Fixed Packages from FastPrototype",
    description: "See the four FastPrototype build packages. Complexity-scored, fixed-price MVP delivery in 1–4 weeks with senior-developer review.",
    keywords: "MVP pricing, fixed price MVP, MVP packages, MVP cost, fixed price development",
    ogType: "website",
    ogTitle: "Fixed-price MVP packages | FastPrototype",
    ogDescription: "Complexity-scored fixed pricing. See what's included in each 1–4 week build package.",
    jsonLd: [
      serviceJsonLd({
        name: "Fixed-price MVP development",
        description: "Complexity-scored, fixed-price MVP delivery in 1–4 weeks with senior-developer review.",
        url: abs("/pricing"),
      }),
      breadcrumbList([{ name: "Home", path: "/" }, { name: "Pricing", path: "/pricing" }]),
    ],
  },
  "/rapid-mvp-development": {
    title: "Rapid MVP Development in 1–4 Weeks | FastPrototype",
    description: "Ship a working, senior-reviewed MVP in 1–4 weeks. AI-generated scaffolding, human-reviewed code, fixed price, full IP ownership.",
    keywords: "rapid MVP development, fast MVP, AI MVP builder, quick MVP, one week MVP",
    ogType: "website",
    ogTitle: "Rapid MVP Development — 1 to 4 Weeks",
    ogDescription: "AI does the scaffolding. Senior developers do the judgement work. You ship in 1–4 weeks.",
    jsonLd: [
      serviceJsonLd({
        name: "Rapid MVP Development",
        description: "Working, senior-reviewed MVP delivered in 1–4 weeks — AI-generated scaffolding paired with human review.",
        url: abs("/rapid-mvp-development"),
      }),
      breadcrumbList([{ name: "Home", path: "/" }, { name: "Rapid MVP Development", path: "/rapid-mvp-development" }]),
    ],
  },
  "/mvp-development-for-startups": {
    title: "MVP Development for Startups | Investor-Ready Software",
    description: "Working MVP software for startups preparing to raise or validate. Fixed price, senior-developer reviewed, full IP transfer on delivery.",
    keywords: "MVP for startups, startup MVP development, investor MVP, pre-seed MVP, fundraising MVP",
    ogType: "website",
    ogTitle: "Startup MVP Development — FastPrototype",
    ogDescription: "Working software before your next investor conversation. 1–4 week delivery, fixed price.",
    jsonLd: [
      serviceJsonLd({
        name: "MVP Development for Startups",
        description: "Investor-ready MVP software delivery — fixed price, senior-reviewed, full IP transfer on delivery.",
        url: abs("/mvp-development-for-startups"),
      }),
      breadcrumbList([{ name: "Home", path: "/" }, { name: "MVP Development for Startups", path: "/mvp-development-for-startups" }]),
    ],
  },
  "/fixed-price-mvp-development": {
    title: "Fixed-Price MVP Development | No Scope Creep",
    description: "Locked pricing before we start. Complexity-scored scope, no surprise invoices, no billable-hours drift. See how our fixed-price model works.",
    keywords: "fixed price MVP, no scope creep, fixed price development, MVP flat rate",
    ogType: "website",
    ogTitle: "Fixed-Price MVP — No Surprises",
    ogDescription: "The proposal price is the invoice price. Every change quoted first.",
    jsonLd: [
      serviceJsonLd({
        name: "Fixed-Price MVP Development",
        description: "Locked pricing before work begins. Every change quoted separately — no billable-hours drift.",
        url: abs("/fixed-price-mvp-development"),
      }),
      breadcrumbList([{ name: "Home", path: "/" }, { name: "Fixed-Price MVP Development", path: "/fixed-price-mvp-development" }]),
    ],
  },
  "/guides/mvp-development-cost": {
    title: "MVP Development Cost in 2026 — Realistic Ranges",
    description: "What MVP development actually costs in 2026, the five price bands you'll encounter, and the two most common ways founders overspend.",
    keywords: "MVP cost, MVP development cost, how much does an MVP cost, MVP pricing guide",
    ogType: "article",
    ogTitle: "MVP Development Cost — 2026 Guide",
    ogDescription: "The five price bands, where the money actually goes, and how to avoid overspending.",
    jsonLd: [
      articleJsonLd({
        headline: "MVP Development Cost in 2026 — Realistic Ranges",
        description: "What MVP development actually costs in 2026, the five price bands you'll encounter, and the two most common ways founders overspend.",
        url: abs("/guides/mvp-development-cost"),
        image: SOCIAL_IMAGE,
        datePublished: CONTENT_LAST_UPDATED_ISO,
        dateModified: CONTENT_LAST_UPDATED_ISO,
      }),
      // BreadcrumbList is emitted by the visible <Breadcrumbs> component on the page.
    ],
  },
  "/guides/how-long-does-an-mvp-take": {
    title: "How Long Does It Take to Build an MVP? 2026 Guide",
    description: "Realistic MVP timelines by build approach, why founders underestimate them, and what actually compresses the calendar.",
    keywords: "how long to build MVP, MVP timeline, MVP delivery time, MVP schedule",
    ogType: "article",
    ogTitle: "How Long an MVP Actually Takes — 2026 Guide",
    ogDescription: "Timelines by approach, why estimates slip, and what actually accelerates delivery.",
    jsonLd: [
      articleJsonLd({
        headline: "How Long Does It Take to Build an MVP? 2026 Guide",
        description: "Realistic MVP timelines by build approach, why founders underestimate them, and what actually compresses the calendar.",
        url: abs("/guides/how-long-does-an-mvp-take"),
        image: SOCIAL_IMAGE,
        datePublished: CONTENT_LAST_UPDATED_ISO,
        dateModified: CONTENT_LAST_UPDATED_ISO,
      }),
    ],
  },
  "/guides/prototype-vs-poc-vs-mvp": {
    title: "Prototype vs Proof of Concept vs MVP — What's the Difference?",
    description: "Three terms, three different questions to answer. When to build each, how much they cost, and a quick decision framework.",
    keywords: "prototype vs MVP, POC vs MVP, MVP definition, proof of concept vs MVP",
    ogType: "article",
    ogTitle: "Prototype vs POC vs MVP — Clear Definitions",
    ogDescription: "What each one tests, when to build each, and how to choose.",
    jsonLd: [
      articleJsonLd({
        headline: "Prototype vs Proof of Concept vs MVP — What's the Difference?",
        description: "Three terms, three different questions to answer. When to build each, how much they cost, and a quick decision framework.",
        url: abs("/guides/prototype-vs-poc-vs-mvp"),
        image: SOCIAL_IMAGE,
        datePublished: CONTENT_LAST_UPDATED_ISO,
        dateModified: CONTENT_LAST_UPDATED_ISO,
      }),
    ],
  },
};

// Ordered list of every route that MUST be pre-rendered + present in sitemap.
export const PRERENDERABLE_ROUTES = Object.keys(SEO_REGISTRY);

export function getSeoForPath(pathname) {
  const path = pathname && pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return SEO_REGISTRY[path || "/"] || null;
}

export function canonicalFor(pathname) {
  const p = pathname && pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
  return `${CANONICAL_ORIGIN}${p || "/"}`;
}
